import moment from 'moment';
import { validateEmail } from 'utils/helper';
import { numberUnmask } from 'utils/unmask';
import {
  blockingSsns,
  blockingEmails,
  blockingFederalTax,
} from 'utils/blockData';

const MerchantApplySchema = {
  businessName: [{
    rule: 'required',
    error: 'The input field is required',
  }],
  businessType: [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'purpose.code': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'businessAddresses.address': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'businessAddresses.city': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'businessAddresses.state': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'businessAddresses.zipCode': [
    {
      isValid: (input) => {
        if (!input) {
          return 'The input field is required';
        }
        return input.replace('_', '').length < 5 ? 'Zipcode is not valid' : true;
      },
    },
  ],
  federalTaxIdOrSSN: [
    {
      isValid: (input) => {
        if (!input) {
          return 'The input field is required';
        }

        if (numberUnmask(input).length < 9 || blockingFederalTax.includes(input)) {
          return 'Tax Id or SSN is not valid';
        }

        return true;
      },
    },
  ],
  yearsUnderCurrentOwnership: [{
    rule: 'required',
    error: 'The input field is required',
  }],
  ownershipType: [{
    rule: 'required',
    error: 'The input field is required',
  }],
  bankName: [{
    isValid: (input, err, opt, formData) => {
      if (!input && (formData.accountHolderName || formData.routingNumber || formData.accountNumber)) {
        return 'The input field is required';
      }
      return true;
    },
  }],
  accountHolderName: [{
    isValid: (input, err, opt, formData) => {
      if (!input && (formData.bankName || formData.routingNumber || formData.accountNumber)) {
        return 'The input field is required';
      }
      return true;
    },
  }],
  routingNumber: [{
    isValid: (input, err, opt, formData) => {
      if (!input && (formData.bankName || formData.accountHolderName || formData.accountNumber)) {
        return 'The input field is required';
      }

      if (!input && !formData.bankName && !formData.accountHolderName && !formData.accountNumber) {
        return true;
      }

      if (`${input}`.replace('_', '').length < 9) {
        return 'Routing Number is not valid';
      }

      return /^(\d)\1+$/.test(input) ? 'Routing Number is not valid' : true;
    },
  }],
  accountNumber: [{
    isValid: (input, err, opt, formData) => {
      if (!input && (formData.bankName || formData.accountHolderName || formData.routingNumber)) {
        return 'The input field is required';
      }

      return /^(\d)\1+$/.test(input) ? 'Account Number is not valid' : true;
    },
  }],
  'principalOwner.firstName': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'principalOwner.lastName': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'principalOwner.address.address': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'principalOwner.address.city': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'principalOwner.address.state': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'principalOwner.address.zipCode': [
    {
      isValid: (input) => {
        if (!input) {
          return 'The input field is required';
        }
        return input.replace('_', '').length < 5 ? 'Zipcode is not valid' : true;
      },
    },
  ],
  'principalOwner.ssn': [{
    isValid: (input, err, opt, formData) => {
      if (!input) {
        return true;
      }

      switch (formData.hasSsn) {
        case true:
          if (numberUnmask(input).length < 4) {
            return 'SSN is not valid';
          }
          break;
        default: {
          if (
            (!formData.hasSSN && numberUnmask(input).length < 9)
            || numberUnmask(input).substring(0, 3) === '000'
            || numberUnmask(input).substring(3, 5) === '00'
            || numberUnmask(input).substring(5, 9) === '0000'
            || (Number(numberUnmask(input).substring(0, 3)) > 899 && Number(numberUnmask(input).substring(0, 3)) < 1000)
            || blockingSsns.includes(input)
          ) {
            return 'SSN is not valid';
          }
        }
      }
      return true;
    },
  }],
  'principalOwner.dateOfBirth': [{
    isValid: (input) => {
      if (!input) {
        return 'The input field is required';
      }

      if (numberUnmask(input).length < 8) {
        return 'Date of Birth is not valid';
      }

      if (!moment(input, 'MM/DD/YYYY').isValid()) {
        return 'Date of Birth is not valid';
      }
      return (moment.duration(moment(new Date()).diff(moment(input, 'MM-DD-YYYY'))).asYears()) < 18 ? 'Must be over 18 years old' : true;
    },
  }],
  'principalOwner.email': [{
    rule: 'required',
    isValid: (input) => {
      if (!input) {
        return 'The input field is required';
      }

      return (
        (blockingEmails.indexOf(`${input}`.toLowerCase()) !== -1)
        || !validateEmail(input)
      ) ? 'Email is not valid' : true;
    },
  }],
  'principalOwner.phoneNumber': [{
    rule: 'required',
    isValid: (input) => {
      if (!input) {
        return 'The input field is required';
      }

      return (
        (input && input.replace(/\D+/g, '').length < 10) ? 'Phone number is not valid' : true
      );
    },
  }],
  'contacts.firstName': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'contacts.lastName': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'contacts.title': [{
    rule: 'required',
    error: 'The input field is required',
  }],
  'contacts.email': [
    'email',
    {
      isValid: (input) => {
        if (!input) {
          return 'The input field is required';
        }

        return blockingEmails.indexOf(input === -1) ? true : 'Email is not valid';
      },
    },
  ],
  'contacts.phoneNumber': [{
    rule: 'required',
    isValid: (input) => {
      if (!input) {
        return 'The input field is required';
      }

      return (
        (input && input.replace(/\D+/g, '').length < 10) ? 'Phone number is not valid' : true
      );
    },
  }],
  discountPolicy: [{
    rule: 'required',
    isValid: (input) => (
      input || 'The input field is required'
    ),
  }],
  merchantAgreement: [{
    rule: 'required',
    isValid: (input) => (
      input || 'The input field is required'
    ),
  }],
  'submittedBy.name': [{
    rule: 'required',
    isValid: (input, err, opt, formData) => {
      if (!input) {
        return 'The input field is required';
      }
      return ((input || '').toLowerCase().replace(/\s/g, '') === `${((formData.principalOwner && formData.principalOwner.firstName) || '').toLowerCase().replace(/\s/g, '')}${((formData.principalOwner && formData.principalOwner.lastName) || '').toLowerCase().replace(/\s/g, '')}`) || 'Signature doesn\'t match';
    },
  }],
  'principalOwner.title': [{
    rule: 'required',
    error: 'The input field is required',
  }],
};

export default MerchantApplySchema;
